export function getTemporalDateRange(dates: Temporal.ZonedDateTime[]) {
    if (dates.length === 0)
        throw new Error("getTemporalDateRange requires a non-empty array");
    let min = dates[0];
    let max = dates[0];
    for (const d of dates.slice(1)) {
        if (Temporal.ZonedDateTime.compare(d, min) < 0) min = d;
        if (Temporal.ZonedDateTime.compare(d, max) > 0) max = d;
    }
    return { min, max };
}

export function getDateRange(dates: Date[]) {
    if (dates.length === 0)
        throw new Error("getDateRange requires a non-empty array");
    const timestamps = dates.map((d) => d.getTime());
    return {
        min: new Date(Math.min(...timestamps)),
        max: new Date(Math.max(...timestamps)),
    };
}

// Overloaded clamp: supports Temporal.ZonedDateTime or Date
export function clamp(
    date: Temporal.ZonedDateTime,
    min: Temporal.ZonedDateTime,
    max: Temporal.ZonedDateTime,
): Temporal.ZonedDateTime;
export function clamp(date: Date, min: Date, max: Date): Date;
export function clamp(
    date: Temporal.ZonedDateTime | Date,
    min: any,
    max: any,
): any {
    if (date instanceof Date) {
        if (date < (min as Date)) return min as Date;
        if (date > (max as Date)) return max as Date;
        return date;
    }
    const zdt = date as Temporal.ZonedDateTime;
    const minZdt = min as Temporal.ZonedDateTime;
    const maxZdt = max as Temporal.ZonedDateTime;
    if (Temporal.ZonedDateTime.compare(zdt, minZdt) < 0) return minZdt;
    if (Temporal.ZonedDateTime.compare(zdt, maxZdt) > 0) return maxZdt;
    return zdt;
}

export function isSameDay(
    d1: Temporal.ZonedDateTime,
    d2: Temporal.ZonedDateTime,
    timezone?: string,
) {
    const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const p1 = d1.withTimeZone(tz).toPlainDate();
    const p2 = d2.withTimeZone(tz).toPlainDate();
    return Temporal.PlainDate.compare(p1, p2) === 0;
}

export function isSameDayDate(d1: Date, d2: Date, timezone?: string) {
    const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const z1 = Temporal.Instant.fromEpochMilliseconds(
        d1.getTime(),
    ).toZonedDateTimeISO(tz);
    const z2 = Temporal.Instant.fromEpochMilliseconds(
        d2.getTime(),
    ).toZonedDateTimeISO(tz);
    return isSameDay(z1, z2, tz);
}

export function isBetween(
    date: Temporal.ZonedDateTime,
    min: Temporal.ZonedDateTime,
    max: Temporal.ZonedDateTime,
) {
    return (
        Temporal.ZonedDateTime.compare(date, min) >= 0 &&
        Temporal.ZonedDateTime.compare(date, max) <= 0
    );
}

export function isBetweenDate(date: Date, min: Date, max: Date) {
    return date >= min && date <= max;
}

export function getDuration(
    from: Temporal.ZonedDateTime,
    to: Temporal.ZonedDateTime,
) {
    return from.until(to);
}

export function getMillisecondsDifference(
    from: Temporal.ZonedDateTime,
    to: Temporal.ZonedDateTime,
) {
    const duration = from.until(to);
    return duration.total("milliseconds");
}

export function getMillisecondsDifferenceDate(from: Date, to: Date) {
    return to.getTime() - from.getTime();
}
